/**
 * Feature Flag Metrics Hook
 *
 * Provides metrics collection and tracking for feature flag rollout monitoring.
 * Tracks color variant usage and aggregates metrics for dashboard display.
 *
 * Features:
 * - trackColorVariant(): Logs analytics event for variant usage
 * - useFeatureFlagMetrics(): Returns stats (total users, legacy count, refined count, percentage)
 * - Queries feature_flag_audit_log for recent changes
 * - Aggregates data by user sessions (distinct user_id)
 * - 5-minute stale time for caching
 * - Supabase-based solution (no external dependencies)
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

// ============================================================================
// Types
// ============================================================================

/**
 * Color variant type
 */
export type ColorVariant = 'legacy' | 'refined';

/**
 * Feature flag audit log entry
 */
export interface FeatureFlagAuditEntry {
  id: number;
  admin_id: string | null;
  flag_name: string;
  action: string;
  old_value: Record<string, unknown>;
  new_value: Record<string, unknown>;
  created_at: string;
}

/**
 * Metrics for feature flag rollout
 */
export interface FeatureFlagMetrics {
  /** Total unique users tracked */
  totalUsers: number;
  /** Users on legacy variant */
  legacyCount: number;
  /** Users on refined variant */
  refinedCount: number;
  /** Percentage of users on refined variant */
  refinedPercentage: number;
  /** Recent flag changes */
  recentChanges: FeatureFlagAuditEntry[];
  /** Current rollout percentage for color_refined flag */
  rolloutPercentage: number;
  /** Whether color_refined flag is enabled */
  isColorRefinedEnabled: boolean;
  /** Baseline metrics captured before rollout */
  baseline: BaselineMetrics | null;
  /** Comparison between current and baseline metrics */
  comparison: MetricsComparison | null;
}

/**
 * Baseline metrics captured before rollout starts
 */
export interface BaselineMetrics {
  /** Timestamp when baseline was captured */
  timestamp: string;
  /** Average session duration (seconds) - placeholder for future analytics */
  avgSessionDuration: number;
  /** Daily active users count */
  dailyActiveUsers: number;
  /** Error rate percentage */
  errorRate: number;
  /** Support ticket volume per day */
  supportTicketsPerDay: number;
}

/**
 * Comparison between current metrics and baseline
 */
export interface MetricsComparison {
  /** Engagement change percentage */
  engagementChange: number;
  /** Error rate change percentage */
  errorRateChange: number;
  /** Support ticket change percentage */
  ticketChange: number;
  /** Overall status: 'healthy', 'warning', or 'critical' */
  status: 'healthy' | 'warning' | 'critical';
  /** Alert messages if any thresholds are exceeded */
  alerts: string[];
}

/**
 * Result of useFeatureFlagMetrics hook
 */
export interface UseFeatureFlagMetricsResult {
  metrics: FeatureFlagMetrics | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

// ============================================================================
// Constants
// ============================================================================

const METRICS_STALE_TIME = 5 * 60 * 1000; // 5 minutes
const COLOR_REFINED_FLAG = 'color_refined';
const RECENT_CHANGES_LIMIT = 10;

// Rollback thresholds (percentage changes from baseline)
const THRESHOLDS = {
  engagementWarning: -5,   // Engagement drop > 5% triggers warning
  engagementRollback: -10,  // Engagement drop > 10% triggers rollback
  errorRateWarning: 10,     // Error rate increase > 10% triggers warning
  errorRateRollback: 20,    // Error rate increase > 20% triggers rollback
  ticketWarning: 25,        // Support tickets increase > 25% triggers warning
  ticketRollback: 50,       // Support tickets increase > 50% triggers rollback
} as const;

// Local storage key for baseline persistence
const BASELINE_STORAGE_KEY = 'feature-flag-baseline-color_refined';

// ============================================================================
// Analytics Tracking (Supabase-based)
// ============================================================================

/**
 * Simple analytics event tracking using Supabase
 * Stores events in a client-side table for aggregation
 *
 * NOTE: This is a lightweight implementation. For production with high traffic,
 * consider using Supabase Edge Functions with proper analytics or a dedicated
 * analytics service.
 */

/**
 * Tracks a color variant assignment for a user
 * Stores the event in Supabase for later aggregation
 *
 * @param userId - The user's ID
 * @param variant - The color variant assigned ('legacy' or 'refined')
 * @returns Promise<boolean> - True if tracking succeeded
 */
export async function trackColorVariant(
  userId: string,
  variant: ColorVariant
): Promise<boolean> {
  try {
    // For now, we'll use the audit log to track variant assignments
    // In a full implementation, you'd want a separate analytics_events table
    // This is a simplified approach that works with the current schema

    // Store in user_feature_flags if not already set (for tracking purposes)
    const { error } = await supabase
      .from('user_feature_flags')
      .upsert({
        user_id: userId,
        flag_name: COLOR_REFINED_FLAG,
        is_enabled: variant === 'refined' ? true : null,
        variant: variant,
      }, {
        onConflict: 'user_id,flag_name',
      });

    if (error) {
      // Don't fail if tracking fails - it's not critical for user experience
      console.warn('Failed to track color variant:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.warn('Error tracking color variant:', error);
    return false;
  }
}

// ============================================================================
// Baseline Metrics
// ============================================================================

/**
 * Gets baseline metrics from localStorage or returns null
 */
function getStoredBaseline(): BaselineMetrics | null {
  if (typeof window === 'undefined') return null;
  try {
    const stored = localStorage.getItem(BASELINE_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored) as BaselineMetrics;
    }
  } catch (error) {
    console.warn('Failed to parse baseline from localStorage:', error);
  }
  return null;
}

/**
 * Stores baseline metrics to localStorage
 */
function setStoredBaseline(baseline: BaselineMetrics): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(BASELINE_STORAGE_KEY, JSON.stringify(baseline));
  } catch (error) {
    console.warn('Failed to store baseline to localStorage:', error);
  }
}

/**
 * Captures baseline metrics for comparison during rollout
 * This should be called before enabling any rollout
 *
 * @returns Baseline metrics
 */
export function captureBaselineMetrics(): BaselineMetrics {
  const baseline: BaselineMetrics = {
    timestamp: new Date().toISOString(),
    // For now, use placeholder values - in production these would come from
    // actual analytics. The key is establishing the structure for comparison.
    avgSessionDuration: 300, // 5 minutes average session
    dailyActiveUsers: 1000,   // Placeholder - will be updated with real data
    errorRate: 2,             // 2% error rate baseline
    supportTicketsPerDay: 5,  // 5 tickets per day baseline
  };

  // Store to localStorage for persistence
  setStoredBaseline(baseline);

  return baseline;
}

/**
 * Compares current metrics against baseline and returns status
 *
 * @param current - Current metrics values
 * @param baseline - Baseline metrics to compare against
 * @returns Comparison result with status and alerts
 */
export function compareMetrics(
  current: Partial<Pick<FeatureFlagMetrics, 'totalUsers' | 'refinedCount'>>,
  baseline: BaselineMetrics
): MetricsComparison {
  const alerts: string[] = [];
  let status: 'healthy' | 'warning' | 'critical' = 'healthy';

  // Calculate changes (placeholder calculations for demonstration)
  // In production, these would use actual analytics data
  const engagementChange = 0; // No change yet without real analytics
  const errorRateChange = 0;
  const ticketChange = 0;

  // Check engagement thresholds
  if (engagementChange <= THRESHOLDS.engagementRollback) {
    status = 'critical';
    alerts.push(`Engagement dropped by ${Math.abs(engagementChange)}% (exceeds ${Math.abs(THRESHOLDS.engagementRollback)}% rollback threshold)`);
  } else if (engagementChange <= THRESHOLDS.engagementWarning) {
    status = status === 'critical' ? 'critical' : 'warning';
    alerts.push(`Engagement dropped by ${Math.abs(engagementChange)}% (monitoring)`);
  }

  // Check error rate thresholds
  if (errorRateChange >= THRESHOLDS.errorRateRollback) {
    status = 'critical';
    alerts.push(`Error rate increased by ${errorRateChange}% (exceeds ${THRESHOLDS.errorRateRollback}% rollback threshold)`);
  } else if (errorRateChange >= THRESHOLDS.errorRateWarning) {
    status = status === 'critical' ? 'critical' : 'warning';
    alerts.push(`Error rate increased by ${errorRateChange}% (monitoring)`);
  }

  // Check support ticket thresholds
  if (ticketChange >= THRESHOLDS.ticketRollback) {
    status = 'critical';
    alerts.push(`Support tickets increased by ${ticketChange}% (exceeds ${THRESHOLDS.ticketRollback}% rollback threshold)`);
  } else if (ticketChange >= THRESHOLDS.ticketWarning) {
    status = status === 'critical' ? 'critical' : 'warning';
    alerts.push(`Support tickets increased by ${ticketChange}% (monitoring)`);
  }

  return {
    engagementChange,
    errorRateChange,
    ticketChange,
    status,
    alerts,
  };
}

/**
 * Logs an alert to the feature_flag_audit_log table
 * This creates a record when rollback thresholds are triggered
 *
 * @param message - Alert message to log
 */
async function logAlert(message: string): Promise<void> {
  try {
    // Insert an audit log entry with action 'alert_triggered'
    // This uses the existing audit_log table structure
    const { error } = await supabase
      .from('feature_flag_audit_log')
      .insert({
        flag_name: COLOR_REFINED_FLAG,
        action: 'alert_triggered',
        old_value: { alert_type: 'rollback_threshold' },
        new_value: { message, timestamp: new Date().toISOString() },
      });

    if (error) {
      console.warn('Failed to log alert:', error);
    }
  } catch (error) {
    console.warn('Error logging alert:', error);
  }
}

// ============================================================================
// Metrics Aggregation
// ============================================================================

/**
 * Aggregates feature flag metrics from Supabase
 * @returns Feature flag metrics
 */
async function fetchFeatureFlagMetrics(): Promise<FeatureFlagMetrics> {
  try {
    // Fetch the color_refined flag status
    const { data: flagData, error: flagError } = await supabase
      .from('feature_flags')
      .select('*')
      .eq('name', COLOR_REFINED_FLAG)
      .maybeSingle();

    if (flagError) throw flagError;

    const isColorRefinedEnabled = flagData?.is_enabled ?? false;
    const rolloutPercentage = flagData?.rollout_percentage ?? 0;

    // Count users with refined variant (user_feature_flags where variant = 'refined')
    const { data: refinedData, error: refinedError } = await supabase
      .from('user_feature_flags')
      .select('user_id', { count: 'exact', head: true })
      .eq('flag_name', COLOR_REFINED_FLAG)
      .eq('variant', 'refined');

    const refinedCount = refinedData ? (refinedData.length || 0) : 0;

    // Count users with legacy variant (user_feature_flags where variant = 'legacy')
    const { data: legacyData, error: legacyError } = await supabase
      .from('user_feature_flags')
      .select('user_id', { count: 'exact', head: true })
      .eq('flag_name', COLOR_REFINED_FLAG)
      .eq('variant', 'legacy');

    const legacyCount = legacyData ? (legacyData.length || 0) : 0;

    // Total unique users with any variant tracked
    const totalUsers = refinedCount + legacyCount;

    // Fetch recent audit log entries
    const { data: auditData, error: auditError } = await supabase
      .from('feature_flag_audit_log')
      .select('*')
      .eq('flag_name', COLOR_REFINED_FLAG)
      .order('created_at', { ascending: false })
      .limit(RECENT_CHANGES_LIMIT);

    const recentChanges = (auditData || []) as FeatureFlagAuditEntry[];

    // Calculate refined percentage
    const refinedPercentage = totalUsers > 0
      ? Math.round((refinedCount / totalUsers) * 100)
      : 0;

    // Get baseline from localStorage
    const baseline = getStoredBaseline();

    // Calculate comparison if baseline exists
    let comparison: MetricsComparison | null = null;
    if (baseline) {
      comparison = compareMetrics({ totalUsers, refinedCount }, baseline);

      // Log any alerts to the audit log
      if (comparison.alerts.length > 0 && comparison.status === 'critical') {
        await logAlert(comparison.alerts.join('; '));
      }
    }

    return {
      totalUsers,
      legacyCount,
      refinedCount,
      refinedPercentage,
      recentChanges,
      rolloutPercentage,
      isColorRefinedEnabled,
      baseline,
      comparison,
    };
  } catch (error) {
    console.error('Error fetching feature flag metrics:', error);
    // Return empty metrics on error
    return {
      totalUsers: 0,
      legacyCount: 0,
      refinedCount: 0,
      refinedPercentage: 0,
      recentChanges: [],
      rolloutPercentage: 0,
      isColorRefinedEnabled: false,
      baseline: null,
      comparison: null,
    };
  }
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * React hook to fetch and track feature flag metrics
 *
 * @returns Feature flag metrics with loading state
 *
 * @example
 * ```tsx
 * const { metrics, isLoading } = useFeatureFlagMetrics();
 *
 * if (isLoading) return <Spinner />;
 * return (
 *   <div>
 *     <p>Refined variant: {metrics?.refinedPercentage}%</p>
 *     <p>Total tracked: {metrics?.totalUsers} users</p>
 *   </div>
 * );
 * ```
 */
export function useFeatureFlagMetrics(): UseFeatureFlagMetricsResult {
  const queryClient = useQueryClient();

  const {
    data: metrics = null,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['featureFlagMetrics', COLOR_REFINED_FLAG],
    queryFn: fetchFeatureFlagMetrics,
    staleTime: METRICS_STALE_TIME,
    refetchOnWindowFocus: true,
    retry: 1,
  });

  /**
   * Invalidates the metrics cache
   * Call this after making changes to feature flags
   */
  const invalidateMetrics = () => {
    queryClient.invalidateQueries({ queryKey: ['featureFlagMetrics'] });
  };

  return {
    metrics,
    isLoading,
    error,
    refetch,
    invalidateMetrics,
  };
}

/**
 * React hook to get aggregated rollout progress for the color_refined flag
 * Returns simplified data suitable for progress bars and charts
 *
 * @example
 * ```tsx
 * const { progress, isLoading } = useColorRolloutProgress();
 *
 * return <Progress value={progress.percentage} />;
 * ```
 */
export function useColorRolloutProgress(): {
  progress: { percentage: number; refined: number; legacy: number; total: number } | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
} {
  const { metrics, isLoading, error, refetch } = useFeatureFlagMetrics();

  const progress = metrics
    ? {
        percentage: metrics.refinedPercentage,
        refined: metrics.refinedCount,
        legacy: metrics.legacyCount,
        total: metrics.totalUsers,
      }
    : null;

  return {
    progress,
    isLoading,
    error,
    refetch,
  };
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Gets the estimated user count for a rollout percentage
 * @param percentage - Rollout percentage (0-100)
 * @param totalUsers - Total user count (defaults to 1000)
 * @returns Estimated number of users
 */
export function estimateUserCount(percentage: number, totalUsers: number = 1000): number {
  return Math.round((percentage / 100) * totalUsers);
}

/**
 * Formats a percentage for display
 * @param value - Percentage value
 * @returns Formatted string (e.g., "75%")
 */
export function formatPercentage(value: number): string {
  return `${Math.round(value)}%`;
}

/**
 * Determines if a user should see the refined color variant
 * Uses consistent hashing based on user ID
 *
 * @param userId - The user's ID
 * @param rolloutPercentage - The rollout percentage (0-100)
 * @returns True if user should see refined variant
 */
export function shouldUserSeeRefinedVariant(userId: string, rolloutPercentage: number): boolean {
  // Simple hash function (same as in featureFlags.ts)
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    const char = userId.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  const userHash = Math.abs(hash) % 100;
  return userHash < rolloutPercentage;
}

/**
 * Sets the rollout percentage for the color_refined feature flag
 * This is the primary function for controlling the gradual rollout
 *
 * @param percentage - Rollout percentage (0-100)
 * @returns True if successful
 */
export async function setColorRolloutPercentage(percentage: number): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('feature_flags')
      .update({
        rollout_percentage: percentage,
        updated_at: new Date().toISOString(),
      })
      .eq('name', COLOR_REFINED_FLAG);

    if (error) {
      console.error('Error setting rollout percentage:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Unexpected error setting rollout percentage:', error);
    return false;
  }
}

/**
 * Enables the color_refined feature flag (100% rollout)
 *
 * @returns True if successful
 */
export async function enableColorRefinedFlag(): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('feature_flags')
      .update({
        is_enabled: true,
        updated_at: new Date().toISOString(),
      })
      .eq('name', COLOR_REFINED_FLAG);

    if (error) {
      console.error('Error enabling color_refined flag:', error);
      return false;
    }

    // Record launch timestamp for post-launch monitoring
    recordLaunchTimestamp();

    return true;
  } catch (error) {
    console.error('Unexpected error enabling color_refined flag:', error);
    return false;
  }
}

/**
 * Disables the color_refined feature flag (rollback to 0%)
 *
 * @returns True if successful
 */
export async function disableColorRefinedFlag(): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('feature_flags')
      .update({
        is_enabled: false,
        rollout_percentage: 0,
        updated_at: new Date().toISOString(),
      })
      .eq('name', COLOR_REFINED_FLAG);

    if (error) {
      console.error('Error disabling color_refined flag:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Unexpected error disabling color_refined flag:', error);
    return false;
  }
}

// ============================================================================
// Rollout Management Hook
// ============================================================================

/**
 * React hook for managing the color rollout percentage
 * Provides functions to update rollout and check status
 *
 * @example
 * ```tsx
 * const { setRolloutPercentage, isUpdating } = useColorRolloutManager();
 *
 * await setRolloutPercentage(25); // Set to 25% rollout
 * ```
 */
export function useColorRolloutManager(): {
  setRolloutPercentage: (percentage: number) => Promise<boolean>;
  enableFullRollout: () => Promise<boolean>;
  rollback: () => Promise<boolean>;
  expandRollout: (targetPercentage: number) => Promise<{ success: boolean; message: string }>;
  isUpdating: boolean;
  error: Error | null;
} {
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const queryClient = useQueryClient();

  const setRolloutPercentage = async (percentage: number): Promise<boolean> => {
    setIsUpdating(true);
    setError(null);

    try {
      const result = await setColorRolloutPercentage(percentage);

      if (result) {
        // Invalidate queries to refresh data
        queryClient.invalidateQueries({ queryKey: ['featureFlagMetrics'] });
        queryClient.invalidateQueries({ queryKey: ['featureFlags'] });
      }

      return result;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error');
      setError(error);
      return false;
    } finally {
      setIsUpdating(false);
    }
  };

  const enableFullRollout = async (): Promise<boolean> => {
    setIsUpdating(true);
    setError(null);

    try {
      const result = await enableColorRefinedFlag();

      if (result) {
        queryClient.invalidateQueries({ queryKey: ['featureFlagMetrics'] });
        queryClient.invalidateQueries({ queryKey: ['featureFlags'] });
      }

      return result;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error');
      setError(error);
      return false;
    } finally {
      setIsUpdating(false);
    }
  };

  const rollback = async (): Promise<boolean> => {
    setIsUpdating(true);
    setError(null);

    try {
      const result = await disableColorRefinedFlag();

      if (result) {
        queryClient.invalidateQueries({ queryKey: ['featureFlagMetrics'] });
        queryClient.invalidateQueries({ queryKey: ['featureFlags'] });
      }

      return result;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error');
      setError(error);
      return false;
    } finally {
      setIsUpdating(false);
    }
  };

  return {
    setRolloutPercentage,
    enableFullRollout,
    rollback,
    expandRollout: async (targetPercentage: number) => {
      setIsUpdating(true);
      setError(null);

      try {
        // Get current metrics for validation
        const { data: currentMetrics } = await queryClient.fetchQuery({
          queryKey: ['featureFlagMetrics', COLOR_REFINED_FLAG],
          queryFn: fetchFeatureFlagMetrics,
        });

        const comparison = currentMetrics?.comparison || null;

        const result = await expandRolloutWithValidation(targetPercentage, comparison);

        if (result.success) {
          queryClient.invalidateQueries({ queryKey: ['featureFlagMetrics'] });
          queryClient.invalidateQueries({ queryKey: ['featureFlags'] });
        }

        return result;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Unknown error');
        setError(error);
        return { success: false, message: error.message };
      } finally {
        setIsUpdating(false);
      }
    },
    isUpdating,
    error,
  };
}

// ============================================================================
// Color Scheme Feedback
// ============================================================================

/**
 * Post-launch metrics for 7-day monitoring
 */
export interface PostLaunchMetrics {
  daysSinceLaunch: number;
  daysUntilStable: number;
  isStable: boolean;
  status: 'success' | 'monitoring' | 'rollback';
  dailyMetrics: Array<{
    date: string;
    engagement: number;
    errorRate: number;
    feedbackCount: number;
  }>;
  cumulativeFeedback: {
    total: number;
    positive: number;
    neutral: number;
    negative: number;
    averageRating: number;
  };
}

/**
 * Launch timestamp for calculating days since launch
 */
const LAUNCH_TIMESTAMP_KEY = 'color-refined-launch-timestamp';

/**
 * Gets the launch timestamp from localStorage
 */
function getLaunchTimestamp(): number {
  if (typeof window === 'undefined') return 0;
  try {
    const stored = localStorage.getItem(LAUNCH_TIMESTAMP_KEY);
    return stored ? parseInt(stored, 10) : 0;
  } catch {
    return 0;
  }
}

/**
 * Sets the launch timestamp in localStorage
 */
function setLaunchTimestamp(): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(LAUNCH_TIMESTAMP_KEY, Date.now().toString());
  } catch (error) {
    console.warn('Failed to store launch timestamp:', error);
  }
}

/**
 * Records the 100% rollout timestamp for post-launch monitoring
 * Call this when expanding to 100% rollout
 */
export function recordLaunchTimestamp(): void {
  setLaunchTimestamp();
}

/**
 * Fetches post-launch metrics for monitoring
 * @returns Post-launch metrics with 7-day trend
 */
async function fetchPostLaunchMetrics(): Promise<PostLaunchMetrics> {
  const launchTimestamp = getLaunchTimestamp();
  const now = Date.now();
  const daysSinceLaunch = launchTimestamp > 0
    ? Math.floor((now - launchTimestamp) / (1000 * 60 * 60 * 24))
    : 0;

  const STABLE_DAYS = 7;
  const daysUntilStable = Math.max(0, STABLE_DAYS - daysSinceLaunch);
  const isStable = daysSinceLaunch >= STABLE_DAYS;

  // Fetch feedback stats
  const feedbackStats = await fetchFeedbackStats();

  // Determine status based on metrics
  let status: 'success' | 'monitoring' | 'rollback' = 'monitoring';

  if (isStable && feedbackStats.positivePercentage >= 60) {
    status = 'success';
  } else if (feedbackStats.positivePercentage < 40 || feedbackStats.negative > feedbackStats.positive) {
    status = 'rollback';
  }

  // Generate daily metrics (placeholder - would come from analytics)
  const dailyMetrics = Array.from({ length: Math.min(daysSinceLaunch || 1, 7) }, (_, i) => {
    const date = new Date(now - (daysSinceLaunch - i - 1) * 24 * 60 * 60 * 1000);
    return {
      date: date.toISOString().split('T')[0],
      engagement: 100 + Math.floor(Math.random() * 20), // Placeholder
      errorRate: 1 + Math.floor(Math.random() * 3), // Placeholder
      feedbackCount: Math.floor(Math.random() * 5), // Placeholder
    };
  }).reverse();

  return {
    daysSinceLaunch,
    daysUntilStable,
    isStable,
    status,
    dailyMetrics,
    cumulativeFeedback: {
      total: feedbackStats.total,
      positive: feedbackStats.positive,
      neutral: feedbackStats.neutral,
      negative: feedbackStats.negative,
      averageRating: feedbackStats.averageRating,
    },
  };
}

/**
 * React hook for fetching post-launch metrics
 *
 * @returns Post-launch metrics with loading state
 *
 * @example
 * ```tsx
 * const { postLaunchMetrics, isLoading } = usePostLaunchMetrics();
 *
 * if (postLaunchMetrics?.isStable) {
 *   return <div>Rollout is stable!</div>;
 * }
 * ```
 */
export function usePostLaunchMetrics(): {
  metrics: PostLaunchMetrics | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
} {
  const {
    data: metrics = null,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['postLaunchMetrics'],
    queryFn: fetchPostLaunchMetrics,
    staleTime: 15 * 60 * 1000, // 15 minutes
    refetchOnWindowFocus: true,
    retry: 1,
  });

  return {
    metrics,
    isLoading,
    error,
    refetch,
  };
}

/**
 * Color scheme feedback entry from the database
 */
export interface ColorSchemeFeedback {
  id: number;
  user_id: string | null;
  variant: 'legacy' | 'refined';
  rating: number;
  sentiment: 'positive' | 'neutral' | 'negative';
  comments: string | null;
  created_at: string;
}

/**
 * Aggregated feedback statistics
 */
export interface FeedbackStats {
  total: number;
  positive: number;
  neutral: number;
  negative: number;
  averageRating: number;
  positivePercentage: number;
  byVariant: {
    legacy: { total: number; averageRating: number };
    refined: { total: number; averageRating: number };
  };
  recentComments: Array<{
    id: number;
    variant: string;
    rating: number;
    sentiment: string;
    comment: string | null;
    createdAt: string;
  }>;
}

/**
 * Fetches aggregated feedback statistics from the database
 */
async function fetchFeedbackStats(): Promise<FeedbackStats> {
  try {
    // Fetch all feedback data
    const { data: feedbackData, error } = await supabase
      .from('color_scheme_feedback')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;

    const feedback = (feedbackData || []) as ColorSchemeFeedback[];

    // Calculate statistics
    const total = feedback.length;
    const positive = feedback.filter(f => f.sentiment === 'positive').length;
    const neutral = feedback.filter(f => f.sentiment === 'neutral').length;
    const negative = feedback.filter(f => f.sentiment === 'negative').length;

    // Calculate average rating
    const totalRating = feedback.reduce((sum, f) => sum + f.rating, 0);
    const averageRating = total > 0 ? Math.round((totalRating / total) * 10) / 10 : 0;

    // Calculate positive percentage
    const positivePercentage = total > 0 ? Math.round((positive / total) * 100) : 0;

    // Calculate by variant
    const legacyFeedback = feedback.filter(f => f.variant === 'legacy');
    const refinedFeedback = feedback.filter(f => f.variant === 'refined');

    const legacyTotalRating = legacyFeedback.reduce((sum, f) => sum + f.rating, 0);
    const refinedTotalRating = refinedFeedback.reduce((sum, f) => sum + f.rating, 0);

    const byVariant = {
      legacy: {
        total: legacyFeedback.length,
        averageRating: legacyFeedback.length > 0
          ? Math.round((legacyTotalRating / legacyFeedback.length) * 10) / 10
          : 0,
      },
      refined: {
        total: refinedFeedback.length,
        averageRating: refinedFeedback.length > 0
          ? Math.round((refinedTotalRating / refinedFeedback.length) * 10) / 10
          : 0,
      },
    };

    // Get recent comments (last 10)
    const recentComments = feedback
      .filter(f => f.comments && f.comments.trim().length > 0)
      .slice(0, 10)
      .map(f => ({
        id: f.id,
        variant: f.variant,
        rating: f.rating,
        sentiment: f.sentiment,
        comment: f.comments,
        createdAt: f.created_at,
      }));

    return {
      total,
      positive,
      neutral,
      negative,
      averageRating,
      positivePercentage,
      byVariant,
      recentComments,
    };
  } catch (error) {
    console.error('Error fetching feedback stats:', error);
    // Return empty stats on error
    return {
      total: 0,
      positive: 0,
      neutral: 0,
      negative: 0,
      averageRating: 0,
      positivePercentage: 0,
      byVariant: {
        legacy: { total: 0, averageRating: 0 },
        refined: { total: 0, averageRating: 0 },
      },
      recentComments: [],
    };
  }
}

/**
 * React hook for fetching color scheme feedback statistics
 *
 * @returns Feedback statistics with loading state
 *
 * @example
 * ```tsx
 * const { stats, isLoading } = useColorSchemeFeedback();
 *
 * return (
 *   <div>
 *     <p>Average Rating: {stats?.averageRating}</p>
 *     <p>Positive: {stats?.positivePercentage}%</p>
 *   </div>
 * );
 * ```
 */
export function useColorSchemeFeedback(): {
  stats: FeedbackStats | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
} {
  const {
    data: stats = null,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['colorSchemeFeedback'],
    queryFn: fetchFeedbackStats,
    staleTime: METRICS_STALE_TIME,
    refetchOnWindowFocus: true,
    retry: 1,
  });

  return {
    stats,
    isLoading,
    error,
    refetch,
  };
}

/**
 * Submits color scheme feedback to the database
 *
 * @param variant - The color variant being reviewed
 * @param rating - Rating from 1-5
 * @param comments - Optional text comment
 * @returns True if submission succeeded
 */
export async function submitColorSchemeFeedback(
  variant: 'legacy' | 'refined',
  rating: number,
  comments?: string
): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      console.error('User not authenticated');
      return false;
    }

    const { error } = await supabase
      .from('color_scheme_feedback')
      .insert({
        user_id: user.id,
        variant,
        rating,
        sentiment: null, // Auto-classified by trigger
        comments: comments?.trim() || null,
      });

    if (error) {
      console.error('Error submitting feedback:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Unexpected error submitting feedback:', error);
    return false;
  }
}

/**
 * Validates metrics against rollback thresholds before expanding rollout
 *
 * @param comparison - Metrics comparison to validate
 * @returns True if metrics are within acceptable range for expansion
 */
export function validateMetricsForExpansion(comparison: MetricsComparison | null): boolean {
  if (!comparison) {
    // No comparison data - proceed with caution
    console.warn('No baseline metrics available for comparison');
    return true;
  }

  // Check if any rollback thresholds were exceeded
  if (comparison.status === 'critical') {
    console.error('Cannot expand rollout: critical metrics exceeded', comparison.alerts);
    return false;
  }

  // Additional validation: check individual metrics
  if (comparison.engagementChange <= THRESHOLDS.engagementRollback) {
    console.error('Engagement drop too large:', comparison.engagementChange);
    return false;
  }

  if (comparison.errorRateChange >= THRESHOLDS.errorRateRollback) {
    console.error('Error rate increase too large:', comparison.errorRateChange);
    return false;
  }

  if (comparison.ticketChange >= THRESHOLDS.ticketRollback) {
    console.error('Support ticket increase too large:', comparison.ticketChange);
    return false;
  }

  return true;
}

/**
 * Checks if rollout can be expanded to the next percentage
 * based on current metrics and time at current percentage
 *
 * @param currentRollout - Current rollout percentage
 * @param comparison - Metrics comparison
 * @param hoursAtCurrent - Hours spent at current percentage
 * @returns Object with canExpand boolean and reason if cannot expand
 */
export function canExpandRollout(
  currentRollout: number,
  comparison: MetricsComparison | null,
  hoursAtCurrent: number
): { canExpand: boolean; reason?: string; suggestedPercentage?: number } {
  // Define rollout stages and minimum observation times
  const rolloutStages = [
    { from: 0, to: 5, minHours: 2 },
    { from: 5, to: 25, minHours: 48 },
    { from: 25, to: 50, minHours: 72 },
    { from: 50, to: 100, minHours: 96 },
  ];

  // Find the current stage
  const currentStage = rolloutStages.find(s => s.from === currentRollout);

  if (!currentStage) {
    // Already at 100% or unknown stage
    return { canExpand: false, reason: 'Rollout already at maximum' };
  }

  // Check minimum observation time
  if (hoursAtCurrent < currentStage.minHours) {
    return {
      canExpand: false,
      reason: `Minimum observation time not met. Need ${currentStage.minHours} hours, currently at ${hoursAtCurrent.toFixed(1)} hours.`,
    };
  }

  // Validate metrics
  if (!validateMetricsForExpansion(comparison)) {
    return {
      canExpand: false,
      reason: 'Metrics exceed rollback thresholds. Review dashboard for details.',
    };
  }

  // Can expand to next stage
  return {
    canExpand: true,
    suggestedPercentage: currentStage.to,
  };
}

/**
 * Attempts to expand rollout to the next percentage
 * Validates metrics before expanding
 *
 * @param targetPercentage - Target rollout percentage
 * @param comparison - Current metrics comparison
 * @returns True if expansion succeeded
 */
export async function expandRolloutWithValidation(
  targetPercentage: number,
  comparison: MetricsComparison | null
): Promise<{ success: boolean; message: string }> {
  // Validate metrics before expanding
  if (!validateMetricsForExpansion(comparison)) {
    return {
      success: false,
      message: 'Cannot expand: metrics exceed rollback thresholds. Review alerts in dashboard.',
    };
  }

  // Valid - proceed with expansion
  const success = await setColorRolloutPercentage(targetPercentage);

  if (success) {
    return {
      success: true,
      message: `Rollout expanded to ${targetPercentage}% successfully. Monitoring continues.`,
    };
  } else {
    return {
      success: false,
      message: 'Failed to update rollout percentage. Check database connection.',
    };
  }
}
